import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CalendarCheck, CheckCircle2 } from "lucide-react";

const TXT = {
  en: { title: "Book your appointment", pick: "Choose a time", name: "Full name", email: "Email", phone: "Phone",
        confirm: "Confirm appointment", done: "Your appointment is booked!", none: "No times are currently available.",
        note: "Booking does not require SMS or calling consent." },
  es: { title: "Reserve su cita", pick: "Elija un horario", name: "Nombre completo", email: "Correo", phone: "Teléfono",
        confirm: "Confirmar cita", done: "¡Su cita está reservada!", none: "No hay horarios disponibles por ahora.",
        note: "La reserva no requiere consentimiento de SMS ni de llamadas." },
};

export default function BookingPage() {
  const { token } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const reactivationToken = searchParams.get("r") || "";
  const leadToken = searchParams.get("lead") || "";
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const bookingToken = useRef("bk_" + Math.random().toString(36).slice(2) + Date.now());

  useEffect(() => { api.get(`/public/book/${token}`).then(({ data }) => setCfg(data)).catch(() => setErr(true)); }, [token]);

  if (err) return <div className="grid min-h-screen place-items-center bg-[#0f2133] p-6 text-center text-white" data-testid="booking-error">Booking is not available for this link.</div>;
  if (!cfg) return <div className="grid min-h-screen place-items-center bg-[#0f2133]"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;
  const L = TXT[cfg.language] || TXT.en;

  const submit = async () => {
    if (!slot || !form.name || !form.phone) return;
    setBusy(true);
    try {
      await api.post(`/public/book/${token}`, { ...form, start: slot, booking_token: bookingToken.current,
        reactivation_token: reactivationToken, lead_token: leadToken });
      setDone(true);
    } catch (e) { alert(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f2133] px-4 py-10 text-white" data-testid="booking-page">
      <div className="mx-auto max-w-lg space-y-6">
        <div className="text-center">
          <CalendarCheck className="mx-auto h-10 w-10 text-[#B28A3D]" />
          <h1 className="mt-3 text-2xl font-semibold">{cfg.agency_name}</h1>
          <p className="text-white/70">{L.title}</p>
        </div>
        {done ? (
          <div className="rounded-xl bg-white/10 p-8 text-center" data-testid="booking-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <p className="mt-3 text-lg">{L.done}</p>
          </div>
        ) : (
          <div className="space-y-4 rounded-xl bg-white/5 p-6">
            <div>
              <div className="mb-2 text-sm text-white/70">{L.pick} ({cfg.timezone})</div>
              {cfg.slots.length === 0 ? <div className="text-sm text-white/60" data-testid="booking-no-slots">{L.none}</div> : (
                <div className="grid max-h-56 grid-cols-2 gap-2 overflow-y-auto">
                  {cfg.slots.map((s) => (
                    <button key={s} onClick={() => setSlot(s)} data-testid={`booking-slot-${s}`}
                      className={`rounded-md border px-2 py-1.5 text-xs ${slot === s ? "border-[#B28A3D] bg-[#B28A3D] text-[#17324D]" : "border-white/20 hover:border-white/50"}`}>
                      {new Date(s).toLocaleString(cfg.language === "es" ? "es-US" : "en-US", { timeZone: cfg.timezone, weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input placeholder={L.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" className="bg-white/10 text-white placeholder:text-white/40" />
            <Input placeholder={L.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" className="bg-white/10 text-white placeholder:text-white/40" />
            <Input placeholder={L.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" className="bg-white/10 text-white placeholder:text-white/40" />
            <p className="text-xs text-white/50">{L.note}</p>
            <Button onClick={submit} disabled={busy || !slot} data-testid="booking-submit" className="w-full bg-[#B28A3D] text-[#17324D] hover:bg-[#c99b45]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L.confirm}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
